/* ===========================================================
   TRM - Live Cupping | the phone

   The phone explains nothing. The projector in front of the
   guest is doing the explaining, and a phone that narrates
   competes with the room and pulls attention away from the
   coffee.

   Waiting is a single slowly pulsing dot. A locked answer is
   the answer filling with ink and one small word. There is no
   green and no red anywhere: right and wrong are fill and
   weight.

   The tasting is self-paced, so this file carries one piece of
   state the server does not: which cup the guest currently has
   open. That belongs here. Seven cups are tasted in whatever
   order the table likes, two guests are never on the same one,
   and if the server held the selection then every reconnect
   would fight the guest for it.
   =========================================================== */

import { $, clear, countUp, h, pad2, paintLogos, reducedMotion, connect } from './common.js';
import { primary, secondary } from '../shared/types.js';
import type { Lang, PlayerState, PublicStation, ServerMessage, Text } from '../shared/types.js';

const TOKEN_KEY = 'trm.cupping.token';

const params = new URLSearchParams(location.search);
let code = (params.get('c') ?? '').replace(/\D/g, '').slice(0, 4);

const mid = $('.mid');
const sheet = $('.sheet');
// The phone scrolls on `.ph`, not the document - see corrections.css.
const ph = $<HTMLElement>('.ph');
const sessionName = $('[data-session]');

/* The footer is detached rather than hidden.
   `.foot` sets `display:flex`, and an author rule beats the user agent's
   `[hidden]{display:none}` - so hiding it the obvious way leaves an empty row
   and its hairline on screen. The reference join screen has no footer element
   at all, so neither does this one. */
const foot = $<HTMLElement>('.foot');
foot.removeAttribute('hidden');
foot.remove();

function showFoot(show: boolean): void {
  if (show) {
    if (!foot.isConnected) sheet.append(foot);
  } else if (foot.isConnected) {
    foot.remove();
  }
}

let token = safeRead(TOKEN_KEY);
let joined = false;

/** Until the first state arrives there is nothing to read the session's choice
    from - so the join screen asks `/api/session` for it before drawing. */
let lang: Lang = 'ar';

/* The join screen is the only screen that exists before a state does, so it is
   the only one carrying its own words. Everything past it is content, and
   content arrives in both languages on the wire.

   The errors are keyed by code rather than taken from the server's `message`:
   the server does not know which language the room is in until a guest is in
   it, and this is a screen a guest only ever sees when something has gone
   wrong - in Al-Rass, in Arabic. */
const JOIN = {
  ar: {
    t1: 'انضم', t2: 'للتذوق', name: 'اسمك', code: 'رمز الجلسة',
    go: 'انضم', digits: 'أربعة أرقام', noName: 'اكتب اسمك',
    locked: 'تم تثبيت الإجابات', cups: 'الأكواب', lock: 'تثبيت الإجابات',
    waitLobby: 'Waiting', waitClosed: 'Waiting',
  },
  en: {
    t1: 'Join the', t2: 'cupping', name: 'Your name', code: 'Session code',
    go: 'Join', digits: 'Four digits.', noName: 'Enter your name.',
    locked: 'Answers locked', cups: 'Cups', lock: 'Lock answers',
    waitLobby: 'Waiting', waitClosed: 'Waiting',
  },
} as const;

const ERRORS: Record<string, { ar: string; en: string }> = {
  'bad-code': { ar: 'أربعة أرقام', en: 'Four digits.' },
  'no-session': { ar: 'لا توجد جلسة بهذا الرمز', en: 'No session on that code.' },
  'bad-name': { ar: 'اكتب اسمك', en: 'Enter your name.' },
  full: { ar: 'القاعة ممتلئة', en: 'The room is full.' },
  'not-host': { ar: 'تعذّر الاتصال', en: 'Something went wrong.' },
  unknown: { ar: 'تعذّر الاتصال', en: 'Something went wrong.' },
};

function words() {
  return JOIN[lang];
}

/** Arabic mirrors the whole sheet, and the join screen has to mirror with it
    before the first state arrives rather than after. */
function setDirection(next: Lang): void {
  lang = next;
  const root = document.documentElement;
  root.lang = next;
  root.dir = next === 'ar' ? 'rtl' : 'ltr';
}

setDirection(lang);

/** The last state the server sent, so a local tap can repaint without waiting for a round trip. */
let last: PlayerState | null = null;

/** What is currently drawn, and under which phase, so `.mid` is only rebuilt when it changed. */
let drawn = '';
let drawnPhase = '';
/** Which screen: the phase and the open cup. Moving between screens starts at the top. */
let drawnView = '';

/** The cup this guest has open, or null for the picker. Local only; never sent. */
let cup: string | null = null;

/** Taps made on this phone, painted before the server has echoed them back. */
const taps: Record<string, number> = {};

/* -- picks ------------------------------------------------
   A guest at the table picks an answer, changes their mind,
   walks to another cup and comes back. Nothing is sent until
   they press the one button on the cup page; then that cup is
   locked, permanently, exactly as before. The picks live only
   on this phone, and survive a reload or a locked screen. */
const PICKS_KEY = 'trm.cupping.picks';

const picks: Record<string, number> = (() => {
  try {
    const parsed = JSON.parse(safeRead(PICKS_KEY) || '{}') as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, number>) : {};
  } catch {
    return {};
  }
})();

function savePicks(): void {
  safeWrite(PICKS_KEY, Object.keys(picks).length ? JSON.stringify(picks) : '');
}

function pick(questionId: string, option: number): void {
  if (!last || answerOf(last, questionId) !== undefined) return;
  picks[questionId] = option;
  savePicks();
  repaint();
}

/** How many of a cup's questions carry a pick that is not locked yet. */
function pickedOn(state: PlayerState, station: PublicStation): number {
  let n = 0;
  for (const question of station.questions) {
    if (answerOf(state, question.id) === undefined && picks[question.id] !== undefined) n += 1;
  }
  return n;
}

/** Lock every pick on this cup: one commitment, then it is a blind tasting again. */
function lockCup(station: PublicStation): void {
  for (const question of station.questions) {
    const option = picks[question.id];
    if (option === undefined) continue;
    delete picks[question.id];
    answer(question.id, option);
  }
  savePicks();
  repaint();
}

/**
 * Squares this phone's optimistic taps against what the server actually holds.
 *
 * A tap was painted as committed the moment it was made, and nothing ever
 * checked that it arrived. It does not always: a socket on stuttering wifi
 * stays OPEN for tens of seconds after the path is dead, so `send` succeeds and
 * the bytes go nowhere; the offline queue drops anything past twelve; and the
 * server refuses an answer outright if the host closed the tasting a moment
 * earlier. In every case the option stayed inked and locked on the guest's
 * screen for the rest of the evening, and scored zero at the reveal.
 *
 * So: while the tasting is open, anything the server has not confirmed is sent
 * again - a state arriving at all means the socket is live. Once it closes, the
 * server's record is the only one that counts and an unconfirmed tap is what it
 * always was, which is not an answer. Better the guest sees they lost one than
 * believes they answered.
 */
function reconcile(state: PlayerState): void {
  let changed = false;
  for (const questionId of Object.keys(picks)) {
    if (state.mine?.[questionId] !== undefined || state.phase !== 'tasting') {
      delete picks[questionId];
      changed = true;
    }
  }
  if (changed) savePicks();

  for (const [questionId, option] of Object.entries(taps)) {
    if (state.mine?.[questionId] !== undefined || state.phase !== 'tasting') {
      delete taps[questionId];
      delete retried[questionId];
      continue;
    }

    /* States arrive whenever anyone in the room taps, so a retry is spaced
       rather than sent on every one of them. */
    const now = Date.now();
    if (now - (retried[questionId] ?? 0) < 1500) continue;
    retried[questionId] = now;
    wire.send({ t: 'answer', questionId, option });
  }
}

/** When each unconfirmed tap was last sent again. */
const retried: Record<string, number> = {};

paintLogos();

/* The reference join screen carries the session name in its top rail, and a
   phone has not joined anything yet at that point - so look it up by code. */
if (code) {
  void fetch(`/api/session/${code}`)
    .then((r) => (r.ok ? r.json() : null))
    .then((info: { name?: Text | string; lang?: Lang } | null) => {
      if (!info) return;

      /* The language is already on this response and was being thrown away, so
         fifty Saudi café owners met an English, left-to-right first screen. */
      if ((info.lang === 'ar' || info.lang === 'en') && info.lang !== lang) {
        setDirection(info.lang);
        if (!joined) renderJoin();
      }

      const name = info.name;
      if (!name || sessionName.textContent) return;
      sessionName.textContent = typeof name === 'string' ? name : primary(name, lang);
    })
    .catch(() => {
      /* the name is decoration here; the join form works without it */
    });
}

/* -- storage ----------------------------------------------
   Private browsing and blocked site data both throw. A guest
   can still play either way; they just cannot survive a reload,
   which is worth degrading for rather than failing on. */

function safeRead(key: string): string {
  try {
    return localStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
}

function safeWrite(key: string, value: string): void {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    /* nothing to do about it */
  }
}

/* -- bilingual --------------------------------------------
   Both readings travel together and the session picks which
   one leads. The other one appears underneath in the small
   technical voice, and only when it says something different -
   a cup labelled "01" in both languages is not bilingual, it
   is just printed twice. */

function markOf(text: Text): HTMLElement | null {
  const other = secondary(text, lang);
  /* This line is the one thing on the sheet running against the page: an
     English sentence in an Arabic session, or the reverse. Left to inherit,
     its question mark ends up on the wrong end of it. */
  return other ? h('p', { class: 'mark stamp', dir: 'auto' }, other) : null;
}

/* ===========================================================
   Joining

   No accounts, no passwords, no email, no app. Scanning the QR
   carries the code, so nearly every guest only ever types a
   name. The code field is there for whoever's camera fails.
   =========================================================== */

function renderJoin(error = ''): void {
  joined = false;
  showFoot(false);
  drawn = 'join';
  drawnPhase = 'join';
  clear(mid);

  const codeField = h('input', {
    class: 'code',
    type: 'text',
    inputmode: 'numeric',
    autocomplete: 'off',
    maxlength: '4',
    placeholder: '0000',
    'aria-label': words().code,
  });

  const nameField = h('input', {
    type: 'text',
    autocomplete: 'name',
    autocapitalize: 'words',
    maxlength: '24',
    placeholder: words().name,
    'aria-label': words().name,
    enterkeyhint: 'go',
  });

  const err = h('p', { class: 'err stamp' }, error);
  // Forward is the way the reader is going, which is the other way in Arabic.
  const button = h(
    'button',
    { class: 'btn', type: 'submit' },
    h('span', {}, words().go),
    h('span', {}, lang === 'ar' ? '←' : '→'),
  );

  const form = h(
    'form',
    {},
    h('h1', { class: 'h1' }, words().t1, h('br'), words().t2),
    code ? null : h('div', { class: 'field' }, codeField),
    h('div', { class: 'field' }, nameField),
    err,
    button,
  );

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    if (!code) {
      const typed = codeField.value.replace(/\D/g, '').slice(0, 4);
      if (typed.length !== 4) {
        err.textContent = words().digits;
        codeField.focus();
        return;
      }
      code = typed;
    }

    const name = nameField.value.trim();
    if (!name) {
      err.textContent = words().noName;
      nameField.focus();
      return;
    }

    err.textContent = '';
    button.setAttribute('disabled', '');
    wire.send({ t: 'join', code, name, ...(token ? { token } : {}) });

    // If the answer never comes, let them try again rather than stranding them.
    setTimeout(() => button.removeAttribute('disabled'), 4000);
  });

  mid.append(form);

  // Only pull the keyboard up when they have already had a go at this.
  if (error) (code ? nameField : codeField).focus();
}

/* ===========================================================
   Tasting
   =========================================================== */

/** The dot, and - when the room asked for it - one quiet line saying what it
    is waiting for. Round 2 (2026-09-17): the manager's testers read the bare dot
    as a broken page. */
function renderWaiting(note = ''): HTMLElement {
  return h('div', { class: 'wait' }, h('span', { class: 'wait__d' }), note ? h('p', { class: 'wait__t stamp' }, note) : null);
}

/** This guest's answer: the server's if it has one, this phone's while it is still in flight. */
function answerOf(state: PlayerState, questionId: string): number | undefined {
  return state.mine?.[questionId] ?? taps[questionId];
}

/** How many of a cup's questions this phone considers answered. */
function answeredOn(state: PlayerState, station: PublicStation): number {
  let n = 0;
  for (const question of station.questions) if (answerOf(state, question.id) !== undefined) n += 1;
  return n;
}

function answer(questionId: string, option: number): void {
  /* A second tap does nothing, permanently. The rendered options are disabled
     once answered, but the guard is here as well: a guest can tap twice inside
     one round trip, and the value of a blind tasting is in committing. */
  if (!last || answerOf(last, questionId) !== undefined) return;

  taps[questionId] = option;
  // Stamped as sent, or the repaint below would send it a second time at once.
  retried[questionId] = Date.now();
  wire.send({ t: 'answer', questionId, option });
  // Paint the commitment now; the network can take its time.
  repaint();
}

/* -- the cup picker ---------------------------------------
   Seven numbered cups on a table, tasted in any order. The
   number is the whole of the information - it is what is
   printed on the cup the guest is holding - so it is the
   largest thing on the tile and sits first, which keeps the
   column of numbers straight whatever the labels do. */

function renderPicker(state: PlayerState): HTMLElement {
  const stations = state.stations ?? [];
  if (stations.length === 0) return renderWaiting();

  const opts = h('div', { class: 'opts' });

  for (const station of stations) {
    /* The server's count is the truth, but a tap made a moment ago has to dim
       its cup now rather than on the next broadcast. */
    const locked = Math.max(state.progress?.[station.id] ?? 0, answeredOn(state, station));
    // Picks count as started - the guest has been here - but only locks finish a cup.
    const given = Math.max(locked, Math.min(station.questions.length, locked + pickedOn(state, station)));
    const done = station.questions.length > 0 && locked >= station.questions.length;

    const label = primary(station.label, lang);
    const other = secondary(station.label, lang);

    /* Part-way through a cup has to look different from not having started it.
       Tasting a coffee and then being pulled into a conversation before
       answering is the likeliest way to lose a cup, and dim-or-not cannot show
       it. A number, in the brand's own counting voice - never a bar. */
    const partial = given > 0 && !done;

    const tile = h(
      'button',
      { class: done ? 'opt is-dim' : 'opt', type: 'button' },
      h('span', { class: 'opt__t' }, pad2(station.number)),
      // A blank label is normal. The second reading stacks under the first
      // rather than beside it, so a long name cannot squeeze the number.
      // Nothing to say about this cup yet: no empty span, no phantom gap.
      partial || label || other
        ? h(
            'span',
            /* No `dir` on the span itself: it also carries the coffee's Arabic
               name and the English reading under it, and forcing the whole
               thing left-to-right reorders a mixed label. Only the count is
               isolated, and only the count needs to be. */
            { class: 'opt__i' },
            partial
              ? h('span', { dir: 'ltr' }, `${given} / ${station.questions.length}`)
              : '',
            partial && label ? h('br') : null,
            label || '',
            other ? h('br') : null,
            other || '',
          )
        : null,
    );

    // A finished cup stays tappable: reviewing what you committed to is the
    // only thing left to do with it.
    tile.addEventListener('click', () => {
      cup = station.id;
      repaint();
    });

    opts.append(tile);
  }

  return h('div', {}, opts);
}

/* -- one cup ----------------------------------------------
   All of a cup's questions on one page. A guest is standing at
   the table with the cup in front of them; paging between two
   or three questions would be three chances to lose the cup
   they are on. */

function renderCup(state: PlayerState, station: PublicStation): HTMLElement {
  const body = h('div', {});

  for (const question of station.questions) {
    const chosen = answerOf(state, question.id);
    const locked = chosen !== undefined;
    const picked = locked ? undefined : picks[question.id];

    const opts = h('div', { class: 'opts' });

    question.options.forEach((option, i) => {
      // The second reading trails the first rather than stacking under it: an
      // option is a word or two. It is only built when there is one - an empty
      // span is still a flex item, and would leave a gap on every row.
      const other = secondary(option, lang);

      /* Locked: the chosen answer fills with ink, the rest fade, nothing taps.
         Picked: the pick fills with ink and the rest stay as they are, still
         live - the guest can change their mind until they lock the cup. */
      const button = h(
        'button',
        {
          class: locked ? (i === chosen ? 'opt is-mine' : 'opt is-dim') : i === picked ? 'opt is-mine' : 'opt',
          type: 'button',
          disabled: locked,
        },
        h('span', { class: 'opt__i' }, pad2(i + 1)),
        h('span', { class: 'opt__t' }, primary(option, lang)),
        other ? h('span', { class: 'opt__i' }, other) : null,
      );

      button.addEventListener('click', () => pick(question.id, i));
      opts.append(button);
    });

    /* Each question is one thing the guest fills in, which is what `.field` is
       and what carries the space between them. */
    body.append(
      h(
        'div',
        { class: 'field' },
        h('h1', { class: 'h2' }, primary(question.prompt, lang)),
        markOf(question.prompt),
        opts,
      ),
    );
  }

  // One small word, when one word is unavoidable, once the cup is finished.
  if (answeredOn(state, station) === station.questions.length) {
    body.append(h('p', { class: 'mark stamp' }, words().locked));
  } else {
    /* The one commitment on the page. It asks for every question on the cup
       to carry a pick first: a cup is locked whole, so a guest never leaves
       one question open by accident and loses it when the tasting closes. */
    const ready = answeredOn(state, station) + pickedOn(state, station) === station.questions.length;
    const lock = h('button', { class: 'btn', type: 'button', disabled: !ready }, h('span', {}, words().lock));
    lock.addEventListener('click', () => lockCup(station));
    body.append(h('div', { class: 'field' }, lock));
  }

  /* The way back, at the bottom, because that is where a guest arrives when
     they have finished the cup - and quiet, because it is not what this screen
     is for. The arrow points the way the reader is going. */
  const back = h(
    'button',
    { class: 'btn btn--ghost', type: 'button' },
    h('span', {}, words().cups),
    h('span', {}, lang === 'ar' ? '→' : '←'),
  );
  back.addEventListener('click', () => {
    cup = null;
    repaint();
  });
  body.append(h('div', { class: 'field' }, back));

  return body;
}

/* ===========================================================
   The reveal

   Only this guest's own outcome, cup by cup, in step with the
   projector. The room's distribution is the projector's job.
   =========================================================== */

function renderReveal(state: PlayerState): HTMLElement {
  const reveal = state.reveal;
  if (!reveal) return renderWaiting();

  /* Scoring is flat - a correct answer is worth the same everywhere and there
     is no speed bonus - so the cup's gain divides evenly across the questions
     this guest had right. */
  const hits = reveal.questions.filter((q) => q.hit).length;
  const each = hits > 0 ? Math.round(reveal.gained / hits) : 0;

  const body = h('div', {});

  /* Which cup this is. The projector carries the number, but a guest looking
     at their phone rather than the wall had no way to tell three correct
     answers from the next cup's three (manager's test, 2026-09-16). The
     number, in the brand's counting voice - never a label. */
  // Not `.num`: in Arabic that class sends a value to the left; this is a name, and sits where the cup tiles put theirs.
  body.append(h('p', { class: 'revnum' }, h('span', { class: 'revnum__w stamp' }, 'CUP'), pad2(reveal.number)));

  for (const question of reveal.questions) {
    /* One question, one `.field` - the same block the cup page answers in.
       The reference reveal had a single question on the screen and needed no
       wrapper; a cup has two or three, and stacked flat the points sit 1.1rem
       under their own block and flush against the next one, which reads as
       though they belong to the wrong answer. */
    body.append(
      h(
        'div',
        { class: 'field' },
        // The pale block is the whole message: filled means you had it.
        h(
          'div',
          { class: question.hit ? 'res is-hit' : 'res' },
          h('p', { class: 'res__v' }, primary(question.answer, lang)),
          markOf(question.answer),
        ),
        /* `.res.is-hit + .res__p` is what inks this number, so the block and
           its points stay adjacent siblings - nothing goes between them. */
        h('p', { class: 'res__p num' }, question.hit ? `+${each}` : '0'),
      ),
    );
  }

  return body;
}

/* ===========================================================
   Standings
   =========================================================== */

function renderBoard(state: PlayerState, end: boolean): HTMLElement {
  const mini = h('div', { class: 'mini mini--bare' });

  /* The ending. Standings and champion used to be the same list on the phone,
     so the last press on the control panel changed nothing a guest could see
     (manager's test, 2026-09-16). Now the winner takes the pale block the
     reveal uses for a correct answer - fill and weight, no words - and the
     list below carries the rest, as on the projector.

     It arrives as a sequence, in the brand's own two moves: the name rises
     and its block fills, the total counts up (the one number that animates),
     then the rest of the board a row per beat, then the mark. Standings still
     arrive as one screen. */
  const rows = state.board ?? [];
  const winner = end ? rows[0] : undefined;
  const staged = end && !reducedMotion();
  const beat = (i: number) => (staged ? `animation-delay:${(1.05 + i * 0.16).toFixed(2)}s` : '');

  const listed = end ? rows.slice(1) : rows;
  listed.forEach((row, i) => {
    // Two guests may share a name; nobody shares a taster number.
    const name = row.ambiguous ? `${row.name} ${pad2(row.taster)}` : row.name;

    mini.append(
      h(
        'div',
        { class: (row.isMe ? 'mini__row stamp is-me' : 'mini__row stamp') + (staged ? ' rise' : ''), style: beat(i) },
        h('span', {}, pad2(row.rank)),
        h('span', { class: 'nm' }, name),
        h('span', { class: 'pt' }, String(row.score)),
      ),
    );
  });

  if (!end) return h('div', {}, mini);

  const total = h('p', { class: 'res__p num' }, staged ? '0' : String(winner ? winner.score : 0));
  const crown = winner
    ? h(
        'div',
        { class: staged ? 'field rise' : 'field' },
        h(
          'div',
          { class: (winner.isMe ? 'res is-hit is-me' : 'res is-hit') + (staged ? ' fill' : '') },
          h('p', { class: 'res__v' }, winner.ambiguous ? `${winner.name} ${pad2(winner.taster)}` : winner.name),
        ),
        total,
      )
    : null;
  if (staged && winner) setTimeout(() => countUp(total, winner.score), 450);

  /* The mark closes it here too.
     A div, not a span: `.endmark` sizes itself with `width` and `aspect-ratio`,
     and neither applies to a non-replaced inline box - as a span it renders
     about four times too big. */
  const body = h(
    'div',
    {},
    crown,
    h('div', { class: crown ? 'field' : '' }, mini),
    h('div', { class: staged ? 'endmark logo rise' : 'endmark logo', 'data-logo': '', style: beat(listed.length + 1.5) }),
  );
  if (staged) body.dataset.staged = '1';
  paintLogos(body);
  return body;
}

/* -- the footer rail --------------------------------------
   Name, taster number, score. The rank appears only once
   something has been revealed - before that it means nothing. */

function renderFoot(state: PlayerState): void {
  showFoot(true);
  clear(foot);

  /* Two guests in a room this size will share a name; nobody shares a taster
     number, so it is the one thing that tells them apart on their own screens. */
  const who = h('span', { class: 'stamp' }, `${state.you.name}  ·  ${pad2(state.you.taster)}`);

  /* Nothing counts down in a self-paced tasting. The slot stays because the
     rail is `space-between` and these three are its layout; `.pclock[hidden]`
     is in the stylesheet for exactly this. */
  const clock = h('span', { class: 'pclock', hidden: true });

  const score = h('span', { class: 'stamp' }, h('strong', {}, String(state.you.score)));
  // Non-breaking: `.stamp` is nowrap, and ASCII spaces would collapse to one.
  if (state.you.rank > 0) score.append(`  ·  ${pad2(state.you.rank)}`);

  foot.append(who, clock, score);
}

/* ===========================================================
   Applying a state
   =========================================================== */

function apply(state: PlayerState): void {
  joined = true;
  last = state;
  // Arabic reads right to left, and the whole sheet mirrors with it.
  setDirection(state.lang);
  reconcile(state);

  sessionName.textContent = primary(state.sessionName, lang);
  renderFoot(state);

  /* A cup can vanish from under the guest: nothing is open outside the
     tasting, and the host can change the cups between sessions. */
  const stations = state.stations ?? [];
  if (state.phase !== 'tasting') cup = null;
  else if (cup !== null && !stations.some((s) => s.id === cup)) cup = null;
  const station = stations.find((s) => s.id === cup) ?? null;

  /* Redrawing on every broadcast would throw away the guest's scroll position
     and restart the transition on every option, several times a minute while
     the room works. */
  const key = [
    state.phase,
    lang,
    cup ?? '-',
    station
      ? station.questions.map((q) => `${q.id}:${answerOf(state, q.id) ?? '-'}:${picks[q.id] ?? '-'}`).join()
      : stations
          .map((s) => `${s.number}:${primary(s.label, lang)}:${answeredOn(state, s)}:${state.progress?.[s.id] ?? 0}:${pickedOn(state, s)}/${s.questions.length}`)
          .join(),
    state.reveal
      ? `${state.reveal.number}/${state.reveal.gained}/${state.reveal.questions.map((q) => (q.hit ? '1' : '0')).join('')}`
      : '',
    (state.board ?? []).map((r) => `${r.rank}${r.name}${r.score}`).join(),
  ].join('|');

  if (key === drawn) return;

  const moved = drawnPhase !== state.phase;
  drawn = key;
  drawnPhase = state.phase;

  clear(mid);

  let body: HTMLElement;
  switch (state.phase) {
    case 'lobby':
      body = renderWaiting(words().waitLobby);
      break;
    case 'closed':
      body = renderWaiting(words().waitClosed);
      break;
    case 'tasting':
      body = station ? renderCup(state, station) : renderPicker(state);
      break;
    case 'reveal':
      body = renderReveal(state);
      break;
    case 'standings':
      body = renderBoard(state, false);
      break;
    case 'champion':
      body = renderBoard(state, true);
      break;
  }

  // Fade and a short rise, on a change of phase and nothing else. Picking up a
  // cup is not an event; it is the guest moving around their own screen.
  // A staged screen (the champion) carries its own beats instead.
  if (moved && !reducedMotion() && !body.dataset.staged) body.classList.add('rise');
  mid.append(body);

  /* A new screen starts at its top. The scroller is `.ph` and it keeps its
     position across a redraw, so a guest who reached cup 07 at the bottom of
     the list opened the cup already scrolled 90px into it. A tap on the same
     screen must not do this - it would yank the page out from under them. */
  const view = `${state.phase}|${cup ?? '-'}`;
  if (view !== drawnView) {
    drawnView = view;
    ph.scrollTop = 0;
  }
}

/** Repaint from the state already in hand, after a tap or a change of cup. */
function repaint(): void {
  if (last) apply(last);
}

/* ===========================================================
   Wire
   =========================================================== */

const wire = connect(
  (msg: ServerMessage) => {
    if (msg.t === 'joined') {
      token = msg.token;
      safeWrite(TOKEN_KEY, token);
      return;
    }

    if (msg.t === 'player') {
      apply(msg);
      return;
    }

    if (msg.t === 'error') {
      /* The server has said this token is finished: the session is over, or it
         is not the session this phone's link names. Keeping it would only send
         the same question again on the next wake. */
      if (msg.code === 'stale') {
        token = '';
        safeWrite(TOKEN_KEY, '');
        renderJoin();
        return;
      }

      /* The link's code is dead - the host started a new session. Without a
         code field the guest's only way back is to rescan, so the field comes
         back, with the error above it. */
      if (msg.code === 'no-session') code = '';

      /* Otherwise the token is deliberately NOT cleared here.
         If the server is restarting, every phone in the room reaches this
         point at once; throwing the token away would cost each guest their
         identity and their score, and they could not rejoin by name because
         the restored session still holds it. Keeping it costs nothing - the
         join below carries it, and the server ignores a token it does not
         recognise. */
      const known = ERRORS[msg.code];
      renderJoin(known ? known[lang] : msg.message);
    }
  },
  () => {
    // Coming back from a locked screen: resume as the same person, silently -
    // but only into the session this link names.
    if (token) wire.send({ t: 'resume', token, ...(code ? { code } : {}) });
    else if (!joined) renderJoin();
  },
);

if (!token) renderJoin();
